import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Search, Plus, Eye, Edit, Trash2, Copy, ChevronLeft, ChevronRight, 
  AppWindow, Server, Database, Link2, Users, Globe, GitBranch,
  ChevronUp, ChevronDown, ChevronsUpDown, ExternalLink
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import ExportButton from '../../components/ExportButton';
import { toast } from 'sonner';

const initialFormData = {
  code: '',
  name: '',
  description: '',
  app_type_id: '',
  status_id: '',
  criticity_id: '',
  url_prod: '',
  url_uat: '',
  url_dev: '',
  url_repo: '',
  url_docs: '',
  technology_stack: '',
  version: '',
  go_live_date: '',
  business_unit: '',
  department: '',
  notes: '',
};

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'code', dir: 'asc' });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ type_id: '', status_id: '', criticity_id: '' });
  
  // Options
  const [formOptions, setFormOptions] = useState({ types: [], statuses: [], criticities: [] });
  const [filterOptions, setFilterOptions] = useState({ types: [], statuses: [], criticities: [] });
  
  // Stats
  const [stats, setStats] = useState({ total: 0, by_status: [], by_type: [], total_interfaces: 0 });
  
  // CRUD State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const { canCreate, canUpdate, canDelete } = usePermissions('apps', 'applications');

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/applications/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/applications/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/applications/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      
      if (searchTerm && searchTerm.length >= 2) params.append('search', searchTerm);
      if (filters.type_id) params.append('type_id', filters.type_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (filters.criticity_id) params.append('criticity_id', filters.criticity_id);
      
      const response = await api.get(`/assets/applications?${params}`);
      setApplications(response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        pages: response.data.pages,
      }));
    } catch (error) {
      toast.error('Error al cargar aplicaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormOptions();
    fetchFilterOptions();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [pagination.page, pagination.limit, sortConfig, searchTerm, filters]);

  // CRUD handlers
  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData(initialFormData);
    setModalOpen(true);
  };

  const handleOpenEdit = (app) => {
    setEditingItem(app);
    setFormData({
      code: app.code || '',
      name: app.name || '',
      description: app.description || '',
      app_type_id: app.app_type_id?.toString() || '',
      status_id: app.status_id?.toString() || '',
      criticity_id: app.criticity_id?.toString() || '',
      url_prod: app.url_prod || '',
      url_uat: app.url_uat || '',
      url_dev: app.url_dev || '',
      url_repo: app.url_repo || '',
      url_docs: app.url_docs || '',
      technology_stack: app.technology_stack || '',
      version: app.version || '',
      go_live_date: app.go_live_date?.split('T')[0] || '',
      business_unit: app.business_unit || '',
      department: app.department || '',
      notes: app.notes || '',
    });
    setModalOpen(true);
  };

  const handleOpenCopy = (app) => {
    setEditingItem(null);
    setFormData({
      code: '',
      name: `${app.name} (copia)`,
      description: app.description || '',
      app_type_id: app.app_type_id?.toString() || '',
      status_id: app.status_id?.toString() || '',
      criticity_id: app.criticity_id?.toString() || '',
      url_prod: '',
      url_uat: '',
      url_dev: '',
      url_repo: app.url_repo || '',
      url_docs: app.url_docs || '',
      technology_stack: app.technology_stack || '',
      version: app.version || '',
      go_live_date: '',
      business_unit: app.business_unit || '',
      department: app.department || '',
      notes: app.notes || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name) {
      toast.error('Código y Nombre son requeridos');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        app_type_id: formData.app_type_id ? parseInt(formData.app_type_id) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        criticity_id: formData.criticity_id ? parseInt(formData.criticity_id) : null,
        go_live_date: formData.go_live_date || null,
      };
      
      if (editingItem) {
        await api.put(`/assets/applications/${editingItem.id}`, payload);
        toast.success('Aplicación actualizada');
      } else {
        await api.post('/assets/applications', payload);
        toast.success('Aplicación creada');
      }
      
      setModalOpen(false);
      fetchApplications();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/applications/${deleteConfirm.id}`);
      toast.success('Aplicación eliminada');
      setDeleteConfirm(null);
      fetchApplications();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        const deps = detail?.dependencies?.join(', ') || 'dependencias activas';
        toast.error(`No se puede eliminar: tiene ${deps}`, { duration: 6000 });
      } else {
        toast.error('Error al eliminar');
      }
    }
  };

  const getStatusBadge = (status, color) => {
    return (
      <span 
        className="px-2 py-1 rounded-full text-xs font-medium text-white"
        style={{ backgroundColor: color || '#6B7280' }}
      >
        {status || 'Sin estado'}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Mapa de Aplicaciones</h1>
            <p className="text-muted-foreground">Inventario y dependencias de aplicaciones</p>
          </div>
          <div className="flex gap-2">
            <ExportButton 
              endpoint="/assets/applications"
              filters={{ search: searchTerm }}
              filename="aplicaciones"
            />
            <Link to="/apps/map">
              <Button variant="outline" className="gap-2">
                <GitBranch className="h-4 w-4" strokeWidth={1.5} />
                Ver Mapa
              </Button>
            </Link>
            {canCreate && (
              <Button onClick={handleOpenCreate} className="gap-2">
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                Nueva App
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <AppWindow className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Apps</p>
                  <p className="text-2xl font-bold text-primary">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Server className="h-5 w-5 text-green-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">En Producción</p>
                  <p className="text-2xl font-bold text-green-500">
                    {stats.by_status?.find(s => s.name === 'En Producción')?.count || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Link2 className="h-5 w-5 text-blue-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Interfaces</p>
                  <p className="text-2xl font-bold text-blue-500">{stats.total_interfaces}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Database className="h-5 w-5 text-yellow-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tipos</p>
                  <p className="text-2xl font-bold text-yellow-500">
                    {stats.by_type?.filter(t => t.count > 0).length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <Input
                    placeholder="Buscar por código, nombre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              
              <Select value={filters.type_id || "all"} onValueChange={(v) => setFilters(p => ({ ...p, type_id: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {filterOptions.types?.map(t => (
                    <SelectItem key={t.id} value={t.id.toString()}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filters.status_id || "all"} onValueChange={(v) => setFilters(p => ({ ...p, status_id: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {filterOptions.statuses?.map(s => (
                    <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filters.criticity_id || "all"} onValueChange={(v) => setFilters(p => ({ ...p, criticity_id: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Criticidad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {filterOptions.criticities?.map(c => (
                    <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select 
                value={pagination.limit.toString()} 
                onValueChange={(v) => setPagination(p => ({ ...p, limit: parseInt(v), page: 1 }))}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium text-xs uppercase cursor-pointer hover:bg-muted" onClick={() => handleSort('code')}>
                      <div className="flex items-center gap-1">CÓDIGO <SortIcon field="code" /></div>
                    </th>
                    <th className="text-left p-3 font-medium text-xs uppercase cursor-pointer hover:bg-muted" onClick={() => handleSort('name')}>
                      <div className="flex items-center gap-1">NOMBRE <SortIcon field="name" /></div>
                    </th>
                    <th className="text-left p-3 font-medium text-xs uppercase">TIPO</th>
                    <th className="text-left p-3 font-medium text-xs uppercase">ESTADO</th>
                    <th className="text-left p-3 font-medium text-xs uppercase">CRITICIDAD</th>
                    <th className="text-center p-3 font-medium text-xs uppercase">
                      <Users className="h-4 w-4 inline" strokeWidth={1.5} />
                    </th>
                    <th className="text-center p-3 font-medium text-xs uppercase">
                      <Server className="h-4 w-4 inline" strokeWidth={1.5} />
                    </th>
                    <th className="text-center p-3 font-medium text-xs uppercase">
                      <Database className="h-4 w-4 inline" strokeWidth={1.5} />
                    </th>
                    <th className="text-center p-3 font-medium text-xs uppercase">
                      <Link2 className="h-4 w-4 inline" strokeWidth={1.5} />
                    </th>
                    <th className="text-right p-3 font-medium text-xs uppercase">ACCIONES</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="10" className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                  ) : applications.length === 0 ? (
                    <tr><td colSpan="10" className="text-center p-8 text-muted-foreground">No hay aplicaciones registradas</td></tr>
                  ) : (
                    applications.map((app) => (
                      <tr key={app.id} className="border-b hover:bg-muted/30 transition-colors" data-testid={`app-row-${app.id}`}>
                        <td className="p-3">
                          <span className="font-mono text-sm font-medium text-primary">{app.code}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="font-medium">{app.name}</span>
                            {app.technology_stack && (
                              <span className="text-xs text-muted-foreground">{app.technology_stack}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-sm">{app.app_type_name || '-'}</span>
                        </td>
                        <td className="p-3">
                          {getStatusBadge(app.status_name, app.status_color)}
                        </td>
                        <td className="p-3">
                          <span className="text-sm">{app.criticity_name || '-'}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-sm text-muted-foreground">{app.owners_count || 0}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-sm text-muted-foreground">{app.servers_count || 0}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-sm text-muted-foreground">{app.databases_count || 0}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-sm text-muted-foreground">{app.interfaces_count || 0}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-end gap-1">
                            <Link to={`/apps/${app.id}`}>
                              <Button variant="ghost" size="icon" className="h-8 w-8" title="Ver detalle">
                                <Eye className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            </Link>
                            {app.url_prod && (
                              <a href={app.url_prod} target="_blank" rel="noopener noreferrer">
                                <Button variant="ghost" size="icon" className="h-8 w-8" title="Abrir URL">
                                  <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
                                </Button>
                              </a>
                            )}
                            {canUpdate && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenEdit(app)} title="Editar">
                                <Edit className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canCreate && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenCopy(app)} title="Copiar">
                                <Copy className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canDelete && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeleteConfirm(app)} title="Eliminar">
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <span className="flex items-center px-3 text-sm">
                    {pagination.page} / {pagination.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                  >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Editar Aplicación' : 'Nueva Aplicación'}</DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Código *</Label>
                <Input 
                  value={formData.code} 
                  onChange={(e) => setFormData(p => ({ ...p, code: e.target.value }))}
                  placeholder="APP-001"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input 
                  value={formData.name} 
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Sistema de Facturación"
                />
              </div>
              
              <div className="space-y-2 col-span-2">
                <Label>Descripción</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Descripción de la aplicación..."
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={formData.app_type_id} onValueChange={(v) => setFormData(p => ({ ...p, app_type_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.types?.map(t => (
                      <SelectItem key={t.id} value={t.id.toString()}>{t.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={formData.status_id} onValueChange={(v) => setFormData(p => ({ ...p, status_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.statuses?.map(s => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Criticidad</Label>
                <Select value={formData.criticity_id} onValueChange={(v) => setFormData(p => ({ ...p, criticity_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.criticities?.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Versión</Label>
                <Input 
                  value={formData.version} 
                  onChange={(e) => setFormData(p => ({ ...p, version: e.target.value }))}
                  placeholder="1.0.0"
                />
              </div>
              
              <div className="space-y-2 col-span-2">
                <Label>Stack Tecnológico</Label>
                <Input 
                  value={formData.technology_stack} 
                  onChange={(e) => setFormData(p => ({ ...p, technology_stack: e.target.value }))}
                  placeholder="React, Node.js, MySQL..."
                />
              </div>
              
              <div className="space-y-2">
                <Label>URL Producción</Label>
                <Input 
                  value={formData.url_prod} 
                  onChange={(e) => setFormData(p => ({ ...p, url_prod: e.target.value }))}
                  placeholder="https://app.empresa.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label>URL Repositorio</Label>
                <Input 
                  value={formData.url_repo} 
                  onChange={(e) => setFormData(p => ({ ...p, url_repo: e.target.value }))}
                  placeholder="https://github.com/..."
                />
              </div>
              
              <div className="space-y-2">
                <Label>Fecha Go-Live</Label>
                <Input 
                  type="date"
                  value={formData.go_live_date} 
                  onChange={(e) => setFormData(p => ({ ...p, go_live_date: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Unidad de Negocio</Label>
                <Input 
                  value={formData.business_unit} 
                  onChange={(e) => setFormData(p => ({ ...p, business_unit: e.target.value }))}
                  placeholder="Finanzas, TI, Ventas..."
                />
              </div>
              
              <div className="space-y-2 col-span-2">
                <Label>Notas</Label>
                <Textarea 
                  value={formData.notes} 
                  onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar Eliminación</DialogTitle>
            </DialogHeader>
            <p>¿Está seguro de eliminar la aplicación <strong>{deleteConfirm?.code} - {deleteConfirm?.name}</strong>?</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Applications;
